/**
 * Typed API errors (ADR-0009/D-2, ADR-0010/D-7). The API answers every failure with RFC 9457 problem details
 * carrying a stable `code`, optional `args`, a `traceId` and, for validation failures, `errors[]` per field. The
 * client never shows server prose: screens translate `code` (namespace `errors`) and show `traceId` for support.
 */

/** One field error of a `validation_failed` problem. */
export interface FieldError {
  /** camelCase field path, e.g. `lines[2].quantity`. */
  field: string;
  /** Stable error code translated by the client. */
  code: string;
  args?: Record<string, unknown>;
}

/** RFC 9457 problem details as produced by the Positive API. */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  instance?: string;
  code?: string;
  traceId?: string;
  args?: Record<string, unknown>;
  errors?: FieldError[];
  [extension: string]: unknown;
}

/** Well-known error codes produced by the platform pipeline and by this client. */
export const errorCodes = {
  validationFailed: "validation_failed",
  notFound: "not_found",
  rateLimited: "rate_limited",
  unexpected: "unexpected_error",
  concurrencyConflict: "concurrency_conflict",
  unauthorized: "unauthorized",
  forbidden: "forbidden",
  network: "network_error",
  invalidResponse: "invalid_response",
} as const;

export interface ApiErrorInit {
  status: number;
  code: string;
  traceId?: string | undefined;
  title?: string | undefined;
  detail?: string | undefined;
  args?: Record<string, unknown> | undefined;
  errors?: readonly FieldError[] | undefined;
  problem?: ProblemDetails | undefined;
  cause?: unknown;
}

/** An HTTP error answered by the API (status ≥ 400) or a transport failure (status 0). */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly traceId: string | undefined;
  readonly title: string | undefined;
  readonly detail: string | undefined;
  readonly args: Readonly<Record<string, unknown>>;
  readonly errors: readonly FieldError[];
  readonly problem: ProblemDetails | undefined;

  constructor(init: ApiErrorInit) {
    super(`${init.code} (HTTP ${String(init.status)})`, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = "ApiError";
    this.status = init.status;
    this.code = init.code;
    this.traceId = init.traceId;
    this.title = init.title;
    this.detail = init.detail;
    this.args = init.args ?? {};
    this.errors = init.errors ?? [];
    this.problem = init.problem;
  }

  /** Field errors grouped by field path, for form libraries. */
  fieldErrors(): Record<string, FieldError[]> {
    const grouped: Record<string, FieldError[]> = {};
    for (const error of this.errors) {
      (grouped[error.field] ??= []).push(error);
    }
    return grouped;
  }
}

/** 400 `validation_failed` with field errors. */
export class ValidationError extends ApiError {
  constructor(init: ApiErrorInit) {
    super(init);
    this.name = "ValidationError";
  }
}

/**
 * 409 Conflict. For optimistic concurrency (`code = concurrency_conflict`, ADR-0010/D-7) the problem carries who
 * changed the record and when; the UI offers Reload instead of silently overwriting.
 */
export class ConflictError extends ApiError {
  constructor(init: ApiErrorInit) {
    super(init);
    this.name = "ConflictError";
  }

  /** True when the conflict is a stale row version (If-Match did not match). */
  get isConcurrencyConflict(): boolean {
    return this.code === errorCodes.concurrencyConflict;
  }

  /** User who changed the record, when the server provides it (`args.changedBy`). */
  get changedBy(): string | undefined {
    const value = this.args["changedBy"];
    return typeof value === "string" ? value : undefined;
  }

  /** Instant of the change, when the server provides it (`args.changedAt`, ISO 8601). */
  get changedAt(): string | undefined {
    const value = this.args["changedAt"];
    return typeof value === "string" ? value : undefined;
  }

  /** Current version (ETag) of the record on the server, when provided. */
  get currentVersion(): string | undefined {
    const value = this.args["currentVersion"];
    return typeof value === "string" ? value : undefined;
  }
}

/** The request never got an HTTP answer (offline, DNS, CORS, aborted by timeout). */
export class NetworkError extends ApiError {
  constructor(cause: unknown) {
    super({ status: 0, code: errorCodes.network, cause });
    this.name = "NetworkError";
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isConflictError(error: unknown): error is ConflictError {
  return error instanceof ConflictError;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readFieldErrors(value: unknown): FieldError[] {
  if (Array.isArray(value)) {
    return value.flatMap((item): FieldError[] => {
      if (!isRecord(item)) return [];
      const field = item["field"];
      const code = item["code"];
      if (typeof field !== "string" || typeof code !== "string") return [];
      const args = item["args"];
      return [{ field, code, ...(isRecord(args) ? { args } : {}) }];
    });
  }
  // ASP.NET's default ValidationProblemDetails shape: { "field": ["message", ...] } — keep the field, code unknown.
  if (isRecord(value)) {
    return Object.keys(value).map((field) => ({ field, code: errorCodes.validationFailed }));
  }
  return [];
}

function defaultCode(status: number): string {
  switch (status) {
    case 400:
      return errorCodes.validationFailed;
    case 401:
      return errorCodes.unauthorized;
    case 403:
      return errorCodes.forbidden;
    case 404:
      return errorCodes.notFound;
    case 409:
      return errorCodes.concurrencyConflict;
    case 429:
      return errorCodes.rateLimited;
    default:
      return status >= 500 ? errorCodes.unexpected : `http_${String(status)}`;
  }
}

/** Maps an HTTP status and (possibly absent or malformed) body to the matching typed error. */
export function toApiError(status: number, body: unknown, fallbackTraceId?: string | null): ApiError {
  const problem = isRecord(body) ? (body as ProblemDetails) : undefined;
  const code = typeof problem?.code === "string" && problem.code !== "" ? problem.code : defaultCode(status);
  const traceId = typeof problem?.traceId === "string" ? problem.traceId : (fallbackTraceId ?? undefined);
  const init: ApiErrorInit = {
    status,
    code,
    traceId,
    title: typeof problem?.title === "string" ? problem.title : undefined,
    detail: typeof problem?.detail === "string" ? problem.detail : undefined,
    args: isRecord(problem?.args) ? problem.args : undefined,
    errors: readFieldErrors(problem?.errors),
    problem,
  };
  if (status === 409 || status === 412) {
    // 412 Precondition Failed is how some stacks answer a stale If-Match; treat it as a concurrency conflict.
    return new ConflictError(status === 412 ? { ...init, code: errorCodes.concurrencyConflict } : init);
  }
  if (status === 400 && code === errorCodes.validationFailed) {
    return new ValidationError(init);
  }
  return new ApiError(init);
}
